import { useEffect } from "react";
import { ColorPicker, Button, Tooltip, Radio, Divider } from "antd";
import {
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import type { FilterType } from "../../../context/VictimsAndKillersContext";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import KillerSelect from "./KillerSelect";
import DateRangeControls from "./DateRangeControls";
import LocationControls from "./LocationControls";
import type { SerialKillerListItemDTO } from "@shared/contracts";
import styled from "styled-components";

type Mode = FilterType;

export interface FiltersRowProps {
  id: number;
  color: string;
  onColorChange: (hex: string) => void;
  killers: SerialKillerListItemDTO[];
  loading?: boolean;
}

// Styled components
const RowSpace = styled.div`
  width: 100%;
  align-items: center;
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

const IconButton = styled(Button)`
  color: #eeeeee !important;
  background: #393e46 !important;
  border: none !important;
  border-radius: 8px;

  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
  }
  &.ant-btn-dangerous {
    color: #eeeeee !important;
  }
  &.ant-btn-dangerous:hover {
    color: #8e1616 !important;
  }
`;

const BottomDivider = styled(Divider)<{ $color: string }>`
  width: 100%;
  margin-top: 4px;
  margin-bottom: 12px;
  &.ant-divider-horizontal {
    border-top: 4px solid ${(p) => p.$color} !important;
    border-radius: 2px;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: inline-flex;
  gap: 8px;

  .ant-radio-button-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: #393e46;
    color: #eeeeee;
    border: 1px solid #222831;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin-right: 0;
  }
  .ant-radio-button-wrapper::before {
    display: none !important;
  }
  .ant-radio-button-wrapper:hover {
    color: #8e1616;
    background: #222831;
    border: 1px solid #8e1616;
  }
  .ant-radio-button-wrapper-checked {
    border-color: #8e1616 !important;
    background: #222831;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const ModeIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const PickerContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
`;

const PickerLabel = styled.span<{ $hidden: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  font-size: 14px;
  color: #eeeeee;
  pointer-events: none;
  user-select: none;
  text-align: center;
  width: 100%;
  text-decoration: ${({ $hidden }) => ($hidden ? "line-through" : "none")};
`;

const WideColorPicker = styled(ColorPicker)`
  width: 100%;
  flex: 1;

  .ant-color-picker-color-block {
    width: 100%;
  }

  .ant-color-picker-trigger {
    background: #eeeeee;
    border-color: #222831;
  }

  .ant-color-picker-trigger:hover {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  justify-content: space-between;
`;

const ActionIcons = styled.div`
  display: inline-flex;
  align-items: flex-end;
  gap: 8px;
`;

const FullWidthRow = styled.div`
  width: 100%;
`;

export default function FiltersRow({
  id,
  color,
  onColorChange,
  killers,
  loading,
}: FiltersRowProps) {
  const {
    filters: rows,
    setFilterKiller: setRowKiller,
    setFilterVisible: setRowVisible,
    removeFilter: removeRow,
    setFilterType: setRowType,
    setFilterFrom: setRowFrom,
    setFilterTo: setRowTo,
    setFilterCountry: setRowCountry,
    setFilterRegion: setRowRegion,
  } = useVictimsAndKillers();
  const { confirm } = useConfirmModal();

  const row = rows.find((r) => r.id === id);

  useEffect(() => {
    if (!row) return;
    if (row.type === "serialkiller") return;
    if (!row.from) setRowFrom(id, dayjs("1960-01-01").toISOString());
    if (!row.to) setRowTo(id, dayjs("1980-01-01").toISOString());
    if (row.country === "USA") setRowCountry(id, "United States");
    if (!row.country) setRowCountry(id, "United States");
    if (!row.region) setRowRegion(id, "Oregon");
  }, [row, id, setRowFrom, setRowTo, setRowCountry, setRowRegion]);

  if (!row) return null;
  const isVisible = row.visible !== false;

  const selectedKillerId = row.killerId ?? null;
  const selectedKillerName =
    killers.find((killer) => killer.id === selectedKillerId)?.name ?? null;
  const mode: Mode = row.type;
  const basePickerLabel =
    selectedKillerName ??
    (mode === "unsolved"
      ? "Unsolved Murder"
      : mode === "missing"
        ? "Missing Person"
        : "Choose color");
  const locationParts = [row.country, row.region].filter(
    (value): value is string => Boolean(value && value.trim()),
  );
  const pickerLabel =
    (mode === "unsolved" || mode === "missing") && locationParts.length > 0
      ? `${basePickerLabel} (${locationParts.join(", ")})`
      : basePickerLabel;

  const defaultFrom = dayjs("1960-01-01");
  const defaultTo = dayjs("1980-01-01");
  const fromValue = row.from ? dayjs(row.from) : defaultFrom;
  const toValue = row.to ? dayjs(row.to) : defaultTo;

  const handleModeChange = (next: Mode) => {
    setRowType(id, next);
    if (next !== "serialkiller" && selectedKillerId) setRowKiller(id, null);
  };

  const handleJumpToMarkers = () => {
    window.dispatchEvent(
      new CustomEvent("fit-markers-for-row", { detail: { rowId: id } }),
    );
  };

  return (
    <RowSpace>
      <PickerContainer>
        <PickerLabel $hidden={!isVisible}>{pickerLabel}</PickerLabel>
        <WideColorPicker
          value={color}
          onChange={(_, hex) => onColorChange(hex)}
        />
      </PickerContainer>

      <ActionsRow>
        <StyledRadioGroup
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as Mode)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="serialkiller">
            <Tooltip title="Serial killer">
              <ModeIcon src="/markers/murder.png" alt="Serial killer" />
            </Tooltip>
          </Radio.Button>
          <Radio.Button value="unsolved">
            <Tooltip title="Unsolved murder">
              <ModeIcon src="/markers/unsolved.png" alt="Unsolved murder" />
            </Tooltip>
          </Radio.Button>
          <Radio.Button value="missing">
            <Tooltip title="Missing persons">
              <ModeIcon src="/markers/missing.png" alt="Missing person" />
            </Tooltip>
          </Radio.Button>
        </StyledRadioGroup>

        <ActionIcons>
          <Tooltip title="Jump to markers">
            <IconButton
              icon={<GlobalOutlined />}
              type="text"
              onClick={handleJumpToMarkers}
            />
          </Tooltip>
          <Tooltip title={isVisible ? "Hide markers" : "Show markers"}>
            <IconButton
              icon={isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              type="text"
              onClick={() => setRowVisible(id, !isVisible)}
            />
          </Tooltip>
          <Tooltip title="Remove crimes">
            <IconButton
              icon={<DeleteOutlined />}
              onClick={() =>
                confirm({
                  question: `Are you sure you want to remove the markers of ${pickerLabel}? `,
                  onOk: () => removeRow(id),
                })
              }
              danger
              type="text"
            />
          </Tooltip>
        </ActionIcons>
      </ActionsRow>

      {mode === "serialkiller" && (
        <KillerSelect
          killers={killers}
          loading={loading}
          value={selectedKillerId}
          onChange={(v) => setRowKiller(id, v)}
        />
      )}

      {(mode === "unsolved" || mode === "missing") && (
        <>
          <FullWidthRow>
            <DateRangeControls
              from={fromValue as Dayjs}
              to={toValue as Dayjs}
              onFromChange={(iso) => setRowFrom(id, iso)}
              onToChange={(iso) => setRowTo(id, iso)}
            />
          </FullWidthRow>

          <FullWidthRow>
            <LocationControls
              country={row.country}
              region={row.region}
              onCountryChange={(v) => setRowCountry(id, v)}
              onRegionChange={(v) => setRowRegion(id, v)}
            />
          </FullWidthRow>
        </>
      )}

      <BottomDivider $color={color} />
    </RowSpace>
  );
}
